<template>
  <!-- Teleport a body: el modal-mask es position:fixed, pero si un ancestro
       tiene transform/filter (p.ej. la card de la vista) se convierte en el
       bloque contenedor del fixed y el overlay queda confinado a la card en
       vez de cubrir todo el viewport. Renderizando en body escapa de eso y
       cubre/difumina la pantalla completa. -->
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show && !dismissed" class="modal-mask user-modal__backdrop">
      <div class="modal-wrapper">
        <div class="modal-container-dashboard user-modal__panel" :class="[{'modal-error': isError}]">
          <!--
            Heart-loader: animación decorativa de 3s antes de mostrar el
            contenido. Se mantiene solo para `type=success` (momento
            celebratorio de plata movida). Para `warning` (rate_changed
            y otros warnings) NO aplica — el user necesita ver el aviso
            y poder reintentar al toque, no esperar un cronómetro
            hardcoded.
          -->
          <div v-if="loading && type === 'success'" class="heart-loader-container">
            <div class="heart-loader"></div>
          </div>
          <div v-else class="modal-content">
            <slot name="close-button">
              <button class="modal-default-button user-modal__close" @click="closeModal" :aria-label="$t('user_dialog.close')"><span class="material-icons-sharp" aria-hidden="true">cancel</span></button>
            </slot>
            <div class="modal-header">
              <h3 class="user-modal__title" :class="[{'title-text-warning': isWarning, 'title-text-error': isError}]">{{ title }}</h3>
            </div>
            <div class="modal-content">
              <p v-html="flashMessage" class="user-modal__body"></p>
              <div class="modal-footer user-modal__actions">
                <button v-show="isSuccess || isWarning" class="text-white shadow-md arcadi-button" @click="closeModal">{{ $t('dashboard.modal.button_accept') }}</button>
                <a v-show="isError || showWhatsapp" target="_blank" :href="`https://wa.me/${whatsappNumber}`" class="whatsapp-contact-button" style="margin-left: 0px;">
                  <span>{{ $t('dashboard.modal.whatsapp') }}</span>
                  <img :src="images.whatsapp" class="inline-block w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<script>
import whatsappIcon from '../../../../assets/images/whatsapp-icon.svg';

export default {
  props: {
    show: Boolean,
    flashMessage: String,
    type: {
      type: String,
      default: 'success',
      required: false,
    },
    whatsappNumber: {
      type: String,
      default: '56940014018',
      required: false,
    },
    // El botón de WhatsApp sale solo con type=error. Con esto sale también en
    // un aviso cuya salida es soporte (el número ya es de otra cuenta).
    showWhatsapp: {
      type: Boolean,
      default: false,
      required: false,
    },
    // Al aceptar/cerrar, recarga la página (default: comportamiento histórico
    // de los modales de dashboard/transferencias, donde recargar refresca el
    // estado). En false solo cierra el modal sin recargar — p.ej. el modal de
    // validación de teléfono, para no perder lo que el usuario tipeó.
    reloadOnClose: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  data() {
    return {
      loading: true,
      dismissed: false,
      images: {
        whatsapp: whatsappIcon,
      }
    };
  },
  created() {
    setTimeout(() => {
      this.loading = false;
    }, 3000);
  },
  computed: {
    isError() {
      return this.type === 'error';
    },
    isSuccess() {
      return this.type === 'success';
    },
    isWarning() {
      return this.type === 'warning';
    },
    title() {
      const titles = {
        success: this.$t('dashboard.modal.congratulations'),
        error: this.$t('dashboard.modal.error'),
        warning: this.$t('dashboard.modal.warning'),
      };
      return titles[this.type] || titles.error;
    },
  },
  methods: {
    closeModal() {
      if (this.reloadOnClose) {
        this.reloadPage();
      } else {
        this.dismissed = true; // solo ocultar, sin recargar
      }
      this.$emit('close');
    },
    reloadPage() {
      location.reload(true); // Recargar la página forzando la recarga desde el servidor
    },
  },
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
  padding: var(--user-modal-gutter);
}

.modal-container-dashboard {
  position: relative;
  margin: 0 auto;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-error {
  --user-modal-shadow: 4px 4px 0 0 #E1374B;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--user-modal-gap);
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container-dashboard,
.modal-leave-to .modal-container-dashboard {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.heart-loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
}

.whatsapp-contact-button{
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-width: 2px;
  box-shadow: 3px 6px 0px 0px #00FF2A;
  border-color: #000;
  border-color: rgba(0, 0, 0, var(--border-opacity));
  background-color: #fff;
  background-color: rgba(255, 255, 255, var(--bg-opacity));
  border-radius: 9999px;
  padding: 0.75rem;
  color: #000;
  color: rgba(0, 0, 0, var(--text-opacity));
  font-size: 16px;
  font-weight: 400;
}

.title-text-warning {
  --user-modal-accent: #F5A623;
}

.title-text-error {
  --user-modal-accent: #E1374B;
}

</style>
